import {Form, Button, Row, Col} from 'react-bootstrap';
import {useState, useEffect, useRef, useCallback} from 'react';
import { useGetSitesQuery } from '../slices/sitesApiSlice';
import { useGetDeviceListMutation } from '../slices/devicesApiSlice';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { AgGridReact } from 'ag-grid-react'; 
import "ag-grid-community/styles/ag-grid.css"; 
import "ag-grid-community/styles/ag-theme-alpine.css"; 
import LanCellRender from '../components/siteDetailComponents/cellRenders/LanCellRender';
import PhoneUpload from '../components/siteDetailComponents/PhoneUpload';
import CellphoneUpload from '../components/siteDetailComponents/CellphoneUpload';
import '../assets/styles/directory.css';
import useDynamicHeight from '../hooks/useDynamicHeight';

const ReportScreen = () => {
    const navigate = useNavigate();
    const {userInfo} = useSelector((state) => state.auth);
    const [phoneUploadVisible, setPhoneUploadVisible] = useState(false);
    const [cellphoneUploadVisible, setCellphoneUploadVisible] = useState(false);
    
    useEffect(() => {
        if(!userInfo){
            navigate('/login');
        }
    }, [userInfo, navigate])

    const {data: sites, isLoading, error} = useGetSitesQuery(undefined, {
        skip: !userInfo
    });
    const [gridContainerRef, gridHeight] = useDynamicHeight([phoneUploadVisible, cellphoneUploadVisible, sites]);
    const [ getDeviceList ] = useGetDeviceListMutation();
    const gridRef = useRef();
    const [rows, setRows] = useState();

    const [quickFilterText, setQuickFilterText] = useState('');

    const onGridReady = (params) => {
        gridRef.current = params.api;
      };

    const onQuickFilterChange = (event) => {
    setQuickFilterText(event.target.value);
    if (gridRef.current && gridRef.current.api) {
        gridRef.current.api.setQuickFilter(event.target.value);
    }
    };


    const [colDefs] = useState([
        {field: "site"},
        {field: "device"},
        {field: "deviceType"},
        {field: "lanIP", cellRenderer: LanCellRender},
        {field: "hostname"},
        {field: "vendor"},
        {field: "wanIP", cellRenderer: LanCellRender},
        {field: "serial"},
        {field: "mac"},
        {field: "number"},
        {field: "user"},
        {field: "supportNumber"},
        {field: "acctNum"}
    ]);

    
    const selectSearch = useCallback(async () => {
        const data = [];
        const selectors = document.querySelectorAll("select");
        let query = "";
        let queryObject = {};
        for(let i = 0; i<selectors.length; i++){
            if(selectors[i].getAttribute("data-type") != null){
                console.log("DATA TYPE: " + selectors[i].getAttribute("data-type"));
                console.log("DATA VALUE: " + selectors[i].value);
                if(selectors[i].value === "all"){
                    continue;
                }
                let innerObject = {$eq: selectors[i].value};
                queryObject[selectors[i].getAttribute('data-type')] = innerObject;
                query = query + `${selectors[i].getAttribute('data-type')}: {$eq: '${selectors[i].value}'}, `
            }
        }
        query = query.substring(0, query.length - 2);
        console.log(queryObject);
        const res = await getDeviceList({query: queryObject});
        const resDevices = res.data?.devices;
        console.log("RES " + resDevices);
        if(resDevices){
            resDevices.forEach((device) => {
                let returnedSite = "";
                if(device.site){
                    returnedSite = device.site.name;
                } else {
                    returnedSite = "NA";
                }
                let addedDevice = {
                    site: returnedSite,
                    device: device.name,
                    deviceType: device.type,
                    vendor: device.vendor,
                    lanIP: device.lanIP,
                    wanIP: device.wanIP,
                    serial: device.serial,
                    mac: device.mac,
                    hostname: device.hostname,
                    acctNum: device.acctNum,
                    supportNumber: device.supportNumber,
                    number: device.number,
                    user: device.user
                };
                data.push(addedDevice);
            });
            setRows(data);
        }
    }, [getDeviceList]);

    useEffect(() => {
        if(userInfo){
            selectSearch();
        }
    }, [userInfo, selectSearch]);


 
    const onExportClick = useCallback(() => {
        const fileName = 'DOCInventory_' + Date.now() + ".csv";
        gridRef.current.exportDataAsCsv({fileName: fileName});
    }, []);

    if (!userInfo) {
        return null;
    }

    return (
        <>
        {isLoading ? (
            <div className="text-center py-5">
                <h2 className="text-white">Loading Inventory...</h2>
            </div>
        ): error ? (
            <div className="text-center py-5">
                <h2 className="text-danger">{error?.data?.message || error.error}</h2>
            </div>
        ) : (
            <>
            <div className='modern-directory-container'>
                
                {/* Header Block */}
                <div className="directory-header-block flex-column flex-md-row align-items-start align-items-md-end gap-3">
                    <div>
                        <div className="directory-tag">Inventory Report</div>
                        <h1 className="directory-title">Global Network Inventory</h1>
                        <p className="directory-subtitle">
                            Search and filter devices, extension numbers, IP addresses, and hardware serials across all sites.
                        </p>
                    </div>
                </div>

                {/* Controls Row */}
                <div className="directory-controls-row mt-4 flex-column flex-sm-row gap-3">
                    <div className="directory-search-col">
                        <Form.Control
                            type="text"
                            placeholder="Filter inventory..."
                            value={quickFilterText}
                            onChange={onQuickFilterChange}
                            className="filter-control"
                        />
                    </div>
                    <div className="directory-actions-col flex-wrap">
                        <Button 
                            className={phoneUploadVisible ? "btn-primary-glass" : "btn-outline-glass"} 
                            onClick={() => setPhoneUploadVisible(!phoneUploadVisible)}
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                                <path d="M21.2 15c.3-1 .2-2.1-.3-3-.5-.9-1.3-1.6-2.3-1.9H18c-.3-1.4-1.2-2.5-2.4-3.1-1.2-.7-2.7-.7-3.9-.1-1.2.5-2 1.6-2.3 2.9h-.4C7.5 9.8 6 10.3 5 11.3c-1.1 1-1.6 2.5-1.5 4"></path>
                                <polyline points="16 16 12 12 8 16"></polyline>
                                <line x1="12" y1="12" x2="12" y2="21"></line>
                            </svg>
                            {phoneUploadVisible ? 'Hide Phone Upload' : 'Upload Phones'}
                        </Button>
                        <Button 
                            className={cellphoneUploadVisible ? "btn-primary-glass" : "btn-outline-glass"} 
                            onClick={() => setCellphoneUploadVisible(!cellphoneUploadVisible)}
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                                <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
                                <line x1="12" y1="18" x2="12.01" y2="18"></line>
                            </svg>
                            {cellphoneUploadVisible ? 'Hide Cellphone Upload' : 'Upload Cellphones'}
                        </Button>
                        <Button className="btn-outline-glass" onClick={onExportClick}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                <polyline points="7 10 12 15 17 10"></polyline>
                                <line x1="12" y1="15" x2="12" y2="3"></line>
                            </svg>
                            Export to CSV
                        </Button>
                    </div>
                </div>
                
                <PhoneUpload 
                    siteId={sites && sites.length > 0 ? sites[0]._id : null} 
                    visible={phoneUploadVisible} 
                />

                <CellphoneUpload 
                    visible={cellphoneUploadVisible}
                />
                
                {/* Inventory Grid */}
                <Row className="mt-3">
                    <Col>
                        <div ref={gridContainerRef} className="ag-theme-alpine" style={{ height: gridHeight }} >
                            <AgGridReact
                                ref={gridRef}
                                rowData={rows}
                                columnDefs={colDefs}
                                suppressExcelExport={true}
                                frameworkComponents={{
                                    LanCellRender: LanCellRender,
                                }}
                                onGridReady={onGridReady}
                                quickFilterText={quickFilterText}
                            />
                        </div>
                    </Col>
                </Row>
            </div>
            </>
        )}
        </>
    )
}

export default ReportScreen;
